import { AssistantCitation } from '@services/modules/AssistantStream'
import dayjs from 'dayjs'
import { makeAutoObservable } from 'mobx'

import { openAssistantRecord, openAssistantRoute } from './assistantNavigation'
import { citationRoute } from './citationRoutes'
import { SESSION_CITATION } from './sessionCitations'

/** A sent email (an email tracking row): opened in its detail modal, not on a screen. */
export const EMAIL_TRACKING_CITATION = 'email_tracking'

/** The email whose detail modal is open (EmailCitationModal shows it). */
class OpenedEmail {
  trackingId: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  open(trackingId: string) {
    this.trackingId = trackingId
  }

  close() {
    this.trackingId = null
  }
}

export const openedEmail = new OpenedEmail()

/** The calendar screen, which opens a session's detail popup from its URL (openSessionId). */
export const CALENDAR_ROUTE = '/view/calendar'

/**
 * The calendar day a session is on, as the answer shows it: the day of its start in its own zone
 * (09:00 in Sydney on 5 October is 22:00 UTC on the 4th).
 */
export const sessionDay = (citation: AssistantCitation): string => {
  const start = dayjs.utc(citation.start ?? citation.label)
  if (!start.isValid()) {
    return citation.start ?? citation.label
  }
  try {
    return (citation.timeZone ? start.tz(citation.timeZone) : start).format(
      'YYYY-MM-DD'
    )
  } catch {
    return start.format('YYYY-MM-DD')
  }
}

/** Whether the record of a citation has a screen (or a modal) this client can open, for this user. */
export const canOpenCitation = (citation: AssistantCitation) =>
  citation.openable !== false &&
  (citation.type === EMAIL_TRACKING_CITATION ||
    citation.type === SESSION_CITATION ||
    !!citationRoute(citation.type, citation.id, citation.label))

/** Opens the record of a server citation in a dashboard tab (SPA navigation, no reload). */
export const openCitation = (citation: AssistantCitation) => {
  if (citation.type === EMAIL_TRACKING_CITATION) {
    openedEmail.open(citation.id)
    return
  }
  if (citation.type === SESSION_CITATION) {
    // The calendar on the session's day, with the session's detail popup open.
    openAssistantRoute(CALENDAR_ROUTE, {
      selectedDate: sessionDay(citation),
      openSessionId: citation.id,
    })
    return
  }
  const route = citationRoute(citation.type, citation.id, citation.label)
  if (route) {
    openAssistantRecord(route)
  }
}
